import { Link } from "react-router-dom";
import { AlertTriangle, MapPin, Users } from "lucide-react";
import { CRISIS_STATS, CHALLENGES, IMG } from "../data/content";
import Seo from "../components/Seo";
import NewsletterSignup from "../components/NewsletterSignup";

export default function SeedCrisis() {
  return (
    <div data-testid="page-seed-crisis">
      <Seo
        title="Australia's native seed supply crisis"
        description="Demand for native seed is outpacing supply across Australia. The structural causes of the native seed crisis, and the systems needed to solve it."
        path="/seed-crisis"
      />
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="specimen-label text-chalk/60 mb-4">Bulletin — Australia&rsquo;s Native Seed Supply Challenge</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              The seed crisis no one is naming.
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-chalk/80 leading-relaxed">
              Australia is committing to large-scale restoration. The seed
              systems needed to deliver it do not yet exist at the scale, quality
              or governance required. This is the missing link.
            </p>
          </div>
          <div className="lg:col-span-4 self-end">
            <div className="stamp">Field Bulletin · No. 01</div>
          </div>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-rule border border-rule">
            {CRISIS_STATS.map((s, i) => (
              <div key={i} className="bg-card p-8 md:p-10 min-h-[260px] flex flex-col justify-between" data-testid={`stat-${i}`}>
                <span className="specimen-label">Indicator {String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-4xl sm:text-5xl md:text-6xl text-ochre2 leading-none my-4">{s.number}</p>
                <p className="text-xs md:text-sm text-muted leading-relaxed">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Challenges — relocated from Home */}
      <section className="border-b border-rule bg-parchment/40" data-testid="crisis-challenges">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">The Problem</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-3 max-w-3xl leading-[1.02]">
            Three Challenges. <em className="text-eucalypt">One Restoration System.</em>
          </h2>
          <p className="text-muted max-w-2xl mb-14 text-sm md:text-base leading-relaxed">
            Across Australia, these challenges are usually addressed by different organisations,
            different funding programs and different teams. Yet each depends on the others.
            Addressing them separately creates gaps. Addressing them together creates capability.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-rule border border-rule">
            {CHALLENGES.map((c, i) => {
              const Icon = [AlertTriangle, MapPin, Users][i];
              return (
                <div key={c.no} className="bg-card p-8 md:p-10 flex flex-col gap-5" data-testid={`challenge-${c.no}`}>
                  <div className="flex items-center justify-between">
                    <span className="specimen-label">No. {c.no}</span>
                    <Icon size={20} className="text-terracotta" />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl leading-[1.05]">{c.title}</h3>
                  <p className="text-sm md:text-base text-ink/80 leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="section-num mb-3">The Structural Causes</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-8 leading-tight">
              Demand without supply. Plans without provenance. Targets without
              people.
            </h2>
            <div className="space-y-6 text-base md:text-lg text-ink/85 leading-relaxed">
              <p>
                Across mine rehabilitation, biodiversity recovery, carbon
                projects and climate adaptation, restoration commitments have
                grown rapidly. Seed supply, by contrast, depends on slow
                biological cycles, dispersed collection, careful provenance and
                governance that holds.
              </p>
              <p>
                Drawing on the work at Uralla, the research record at Kings Park
                and the experience of Indigenous land managers, the picture is
                consistent: the systems we need are not yet built.
              </p>
              <p>
                SeedKeepers exists to build them — at the scale, with the
                people, and on the timeline the work actually requires.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={IMG.seedsBowl} alt="Bowl of native seed" className="w-full h-[420px] object-cover border border-rule p-2 bg-cream" />
            <p className="mt-3 text-xs text-muted italic">Mulga seed — 150 kilograms. Field collection.</p>
          </div>
        </div>
      </section>

      {/* Video — A global mirror of the crisis */}
      <section className="border-b border-rule bg-parchment/40" data-testid="crisis-video">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="section-num mb-3">A Global Mirror</p>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.05] mb-6">
              The same crisis, half a world away.
            </h2>
            <p className="text-sm md:text-base text-ink/80 leading-relaxed mb-4">
              This short film focuses on <strong>North America</strong>, where restoration
              practitioners, Indigenous seed keepers and ecologists are naming the
              same structural gap: demand for native seed is racing ahead of the
              systems that can supply it.
            </p>
            <p className="text-sm md:text-base text-ink/80 leading-relaxed">
              The species differ. The landscapes differ. The problem, and the
              solution, are the same — <em>build the seed system underneath the
              restoration promise.</em>
            </p>
            <p className="mt-6 specimen-label text-muted">Source · YouTube — external</p>
          </div>
          <div className="lg:col-span-7">
            <div className="relative w-full border border-rule p-2 bg-card" style={{ paddingBottom: "calc(56.25% + 1rem)" }}>
              <iframe
                className="absolute inset-2 w-[calc(100%-1rem)] h-[calc(100%-1rem)]"
                src="https://www.youtube.com/embed/QqBlfTOt7Jo?si=hNEy9pWwKulezWSD"
                title="The native seed crisis — North America"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                data-testid="crisis-video-iframe"
              />
            </div>
            <p className="mt-3 text-xs text-muted italic">
              Primary focus: North America. The Australian context differs in species and Country, but the structural challenge is the same.
            </p>
          </div>
        </div>

        {/* Quiet newsletter capture — for those moved to act */}
        <div className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-24 -mt-4">
          <NewsletterSignup source="seed-crisis" />
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-6">What Now</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight mb-10">
            If your organisation is investing in restoration, talk to us about
            the seed system underneath it.
          </h2>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            data-testid="crisis-cta"
          >
            Start a conversation
          </Link>
        </div>
      </section>
    </div>
  );
}
